import { matchByIndex } from '../string/string';

/**
 * Navegadores WEB.
 *
 * @category Platform
 */
export type UserAgent = 'Chrome' | 'Edge' | 'Safari' | 'Firefox' | 'Internet Explorer';

/**
 * Sistemas operacionais.
 *
 * @category Platform
 */
export type OS = 'Windows' | 'Android' | 'IOS' | 'MacOS';

/**
 * Todas as informações para identificar o aparelho e o navegador estão no objeto `platform`.
 *
 * @category Platform
 */
export interface Platform {
  /** identifica se o aparelho é um aparelho móvel */
  isMobile: boolean;
  /** Sistemas operacionais. */
  os: OS;
  /** Navegadores WEB. */
  userAgent: UserAgent;
  /** Versão do navegador. */
  userAgentVersion: string;
  /** Versão do sistema operacional. */
  osVersion: string;
  /** Verifica se está executando do lado do cliente. */
  isDOM: boolean;
}

interface UserAgentDataBrand {
  brand: string;
  version: string;
}

interface UserAgentData {
  brands: UserAgentDataBrand[];
  mobile: boolean;
  platform: 'Windows' | 'Android' | 'macOS';
}

declare global {
  interface Navigator {
    userAgentData?: UserAgentData;
  }
}

const isIpad = (): boolean =>
  // o iPad 13 vem descrito como Mac OS no seu sistema operacional, porém é
  // possível diferenciar pelos pontos de toque na tela

  navigator.userAgent.includes('iPad') ||
  (!navigator.userAgent.includes('iPhone') &&
    navigator.userAgent.includes('Mac OS') &&
    navigator.maxTouchPoints > 1);

const getOs = (): OS => {
  if (
    navigator.userAgentData?.platform === 'Windows' ||
    navigator.userAgent.includes('Windows NT')
  ) {
    return 'Windows';
  }

  if (navigator.userAgentData?.platform === 'Android' || navigator.userAgent.includes('Android')) {
    return 'Android';
  }

  if (navigator.platform === 'iPhone' || navigator.userAgent.includes('iPhone') || isIpad()) {
    return 'IOS';
  }

  if (
    navigator.userAgentData?.platform === 'macOS' ||
    navigator.userAgent.includes('Mac OS') ||
    /* istanbul ignore next */
    navigator.platform === 'MacIntel'
  ) {
    return 'MacOS';
  }

  /* istanbul ignore next */
  return 'Windows';
};

const getIsMobile = (): boolean => {
  const os = getOs();

  if (navigator.userAgentData?.mobile || os === 'Android' || os === 'IOS') {
    return true;
  }

  return false;
};

const getUserAgent = (): UserAgent => {
  // A única informação que difere o Chrome do Safari no IOS é a descrição CriOS
  // https://chromium.googlesource.com/chromium/src.git/+/HEAD/docs/ios/user_agent.md
  const isIphoneChrome = navigator.userAgent.includes('CriOS');

  // Contando que existe apenas Chrome e Safari no iPhone, excluindo Chrome,
  // sobra Safari
  const isIphoneSafari = !isIphoneChrome && (navigator.platform === 'iPhone' || isIpad());

  const isMacSafari =
    (navigator.vendor || '').includes('Apple Computer, Inc') &&
    (navigator.platform === 'MacIntel' || navigator.userAgent.includes('Macintosh'));

  if (
    navigator.userAgentData?.brands?.some(
      ({ brand }: { brand: string }) => brand === 'Microsoft Edge',
    ) ||
    navigator.userAgent.includes('Edg') ||
    navigator.userAgent.includes('EdgiOS')
  ) {
    return 'Edge';
  }

  if (
    isIphoneChrome ||
    navigator.userAgentData?.brands?.some(
      ({ brand }: { brand: string }) => brand === 'Google Chrome',
    )
  ) {
    return 'Chrome';
  }

  if (navigator.userAgent.includes('Firefox/') || navigator.userAgent.includes('FxiOS/')) {
    return 'Firefox';
  }

  if (isIphoneSafari || isMacSafari) {
    return 'Safari';
  }

  return 'Internet Explorer';
};

const getBrowserVersion = () => {
  const os = getOs();

  const alias: Record<UserAgent, string> = {
    Chrome: `${os === 'IOS' ? 'CriOS' : 'Chrome'}`,
    Edge: `${os === 'IOS' ? 'EdgiOS' : 'Edg'}`,
    Firefox: `${os === 'IOS' ? 'FxiOS' : 'Firefox'}`,
    'Internet Explorer': 'rv',
    Safari: 'Version',
  };

  const browser = alias[getUserAgent()];
  const pattern = new RegExp(browser !== 'rv' ? `${browser}\\/(\\d+)` : `${browser}:(\\d+)`);
  const version = matchByIndex(pattern, navigator.userAgent, 1);

  return version;
};

const getOsVersion = () => {
  const userAgent = navigator.userAgent;
  const versionPart = userAgent.match(/\((.*?)\)/)?.[1].replace(/_/g, '.') || '';

  if (isIpad()) {
    const ipadOs = userAgent.includes('CPU OS') ? 'CPU OS' : 'Mac OS X';

    return matchByIndex(new RegExp(`${ipadOs} (\\d+(?:\\.\\d+)*)`), versionPart, 1);
  }

  const os = getOs();
  const osRecord: Record<OS, string> = {
    Android: 'Android',
    IOS: 'iPhone OS',
    MacOS: 'Mac OS X',
    Windows: 'Windows NT',
  };

  return matchByIndex(new RegExp(`${osRecord[os]} (\\d+(?:\\.\\d+)*)`), versionPart, 1);
};

/**
 * Verifica se está executando do lado do cliente. Isto evita erros em SSR não ter a variável document
 * https://stackoverflow.com/questions/32216383/in-react-how-do-i-detect-if-my-component-is-rendering-from-the-client-or-the-se
 */
export const getIsDOM = () =>
  !!(typeof window !== 'undefined' && window.document && window.document.createElement);

/**
 * Todas as informações para identificar o aparelho e o navegador estão no objeto `platform`.
 *
 * @category Platform
 *
 * @example
 * ```ts
 * import { platform } from '@ids/tools';
 *
 * // aparelho android e navegador Chrome
 * platform.isMobile // true
 * platform.os // 'Android'
 * platform.userAgent // 'Chrome'
 * platform.userAgentVersion // '112'
 * platform.osVersion // '(Linux; Android 11; SM-A515F Build/RP1A.200720.012)'
 * platform.isDOM // true
 * ```
 */
export const platform: Platform = {
  get isMobile() {
    return getIsMobile();
  },
  get os() {
    return getOs();
  },
  get userAgent() {
    return getUserAgent();
  },
  get userAgentVersion() {
    return getBrowserVersion();
  },
  get osVersion() {
    return getOsVersion();
  },
  get isDOM() {
    return getIsDOM();
  },
};
